import Phaser from 'phaser';
import {
  COLORS,
  DEPTHS,
  DISPUTE_FORM_MODAL,
  EMAIL_ACTION_FOOTER,
  FONTS,
  GAME_HEIGHT,
  GAME_WIDTH,
  toHexColor,
} from '../config/gameConfig';
import type { Email, EmailAction, EventBus } from '../systems/EventBus';
import { COPY } from '../content/copy';
import { AudioManager } from '../systems/AudioManager';
import { autoMark } from '../ui/autoMark';
import { renderMarkedText } from '../ui/MarkedText';
import type { Tooltip } from '../ui/Tooltip';
import type { EmailCategory } from '../content/emails';
import { actionsForCategory, classifyAction, ACTION_LABELS } from '../content/email-actions';
import { REPLY_TEMPLATES, type ReplyTemplate } from '../content/reply-templates';

const MODAL_WIDTH = 560;
const MODAL_HEIGHT = 420;
const MODAL_PADDING_X = 24;
const MODAL_PADDING_Y = 20;
const CLOSE_BUTTON_SIZE = 28;

export class EmailReaderModal extends Phaser.GameObjects.Container {
  private readonly onDismissCallback: () => void;
  private readonly bus?: EventBus;
  private readonly tooltip?: Tooltip;
  private readonly email: Email;
  private dismissed = false;
  private composeOverlay: Phaser.GameObjects.Container | null = null;

  /** Phaser doesn't propagate container depth to children for input
   *  hit-testing. Without this, the modal's interactive chrome lands at
   *  depth 0 while underlying app chromes sit at 20+, so clicks bleed
   *  through. Walk the tree and force every descendant to the modal
   *  depth (or higher for compose). */
  private propagateDepth(target: Phaser.GameObjects.GameObject, depth: number): void {
    const t = target as Phaser.GameObjects.GameObject & {
      setDepth?: (d: number) => unknown;
      list?: Phaser.GameObjects.GameObject[];
    };
    t.setDepth?.(depth);
    if (Array.isArray(t.list)) {
      for (const child of t.list) this.propagateDepth(child, depth);
    }
  }
  private readonly escHandler = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      if (this.composeOverlay) this.closeCompose();
      else this.dismiss();
    }
  };

  constructor(scene: Phaser.Scene, email: Email, onDismiss: () => void, bus?: EventBus, tooltip?: Tooltip) {
    const x = (GAME_WIDTH - MODAL_WIDTH) / 2;
    const y = (GAME_HEIGHT - MODAL_HEIGHT) / 2;
    super(scene, x, y);
    scene.add.existing(this);
    this.setDepth(DEPTHS.modal);
    this.onDismissCallback = onDismiss;
    this.bus = bus;
    this.tooltip = tooltip;
    this.email = email;

    // Soft dim around the modal card — non-interactive so clicks on
    // underlying windows (e.g. the AR aging spreadsheet) pass through.
    // Players need to be able to look up data while composing a reply.
    // Closing the modal goes through the X button or ESC.
    const backdrop = scene.add
      .rectangle(-x, -y, GAME_WIDTH, GAME_HEIGHT, COLORS.black, 0.35)
      .setOrigin(0, 0);
    this.add(backdrop);

    // Card.
    const border = scene.add.rectangle(0, 0, MODAL_WIDTH, MODAL_HEIGHT, COLORS.windowBorder).setOrigin(0, 0);
    this.add(border);

    const chrome = scene.add.rectangle(1, 1, MODAL_WIDTH - 2, MODAL_HEIGHT - 2, COLORS.windowBody).setOrigin(0, 0);
    chrome.setInteractive(); // Swallow clicks so the backdrop doesn't dismiss when clicking the card.
    this.add(chrome);

    // Subject.
    const subjectText = scene.add.text(MODAL_PADDING_X, MODAL_PADDING_Y, email.subject, {
      fontFamily: FONTS.ui,
      fontSize: '18px',
      color: toHexColor(COLORS.emailSubject),
      fontStyle: 'bold',
      wordWrap: { width: MODAL_WIDTH - MODAL_PADDING_X * 2 - CLOSE_BUTTON_SIZE - 8 },
    }).setOrigin(0, 0);
    this.add(subjectText);

    // From / receivedAt meta line.
    const metaY = MODAL_PADDING_Y + subjectText.height + 6;
    const metaText = scene.add.text(MODAL_PADDING_X, metaY, `${email.from} · ${email.receivedAt}`, {
      fontFamily: FONTS.ui,
      fontSize: '12px',
      color: toHexColor(COLORS.emailMeta),
    }).setOrigin(0, 0);
    this.add(metaText);

    // Divider.
    const divY = metaY + metaText.height + 10;
    const divider = scene.add.rectangle(MODAL_PADDING_X, divY, MODAL_WIDTH - MODAL_PADDING_X * 2, 1, COLORS.emailSeparator).setOrigin(0, 0);
    this.add(divider);

    // Body — route through MarkedText if tooltip + bus available, else plain text.
    if (tooltip && bus) {
      renderMarkedText(
        scene,
        bus,
        tooltip,
        this,
        MODAL_PADDING_X,
        divY + 12,
        autoMark(email.body),
        {
          fontSize: '14px',
          color: toHexColor(COLORS.textPrimary),
        },
        { wrapWidth: MODAL_WIDTH - MODAL_PADDING_X * 2 },
      );
    } else {
      const bodyText = scene.add.text(MODAL_PADDING_X, divY + 12, email.body, {
        fontFamily: FONTS.ui,
        fontSize: '14px',
        color: toHexColor(COLORS.textPrimary),
        wordWrap: { width: MODAL_WIDTH - MODAL_PADDING_X * 2 },
        lineSpacing: 4,
      }).setOrigin(0, 0);
      this.add(bodyText);
    }

    // Close button (top-right X).
    const closeX = MODAL_WIDTH - MODAL_PADDING_X - CLOSE_BUTTON_SIZE / 2;
    const closeY = MODAL_PADDING_Y + CLOSE_BUTTON_SIZE / 2 - 4;
    const closeBtn = scene.add.circle(closeX, closeY, CLOSE_BUTTON_SIZE / 2, COLORS.windowControlClose);
    closeBtn.setInteractive({ useHandCursor: true });
    closeBtn.on('pointerdown', () => this.dismiss());
    this.add(closeBtn);

    const closeIcon = scene.add.image(closeX, closeY, 'icon:x')
      .setDisplaySize(14, 14)
      .setOrigin(0.5, 0.5);
    this.add(closeIcon);

    // ESC key binding.
    scene.input.keyboard?.on('keydown', this.escHandler);

    // Footer: dispute hook takes precedence; otherwise category-based triage actions.
    const needsDisputeButton = email.disputeHook && !email.loggedAsDispute;
    const needsLoggedBadge = email.loggedAsDispute === true;
    if (needsDisputeButton || needsLoggedBadge) {
      this.renderDisputeFooter(scene, email);
    } else {
      this.renderActionFooter(scene, email);
    }

    // Clean up on scene lifecycle.
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.dismiss());
    scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.dismiss());

    AudioManager.getInstance().playModalOpen();

    // Lift every descendant to the modal depth so Phaser's input
    // hit-test puts modal content above the underlying app windows.
    this.propagateDepth(this, DEPTHS.modal);
  }

  private renderDisputeFooter(scene: Phaser.Scene, email: Email): void {
    const footerY = MODAL_HEIGHT - DISPUTE_FORM_MODAL.readerFooterHeight;
    const divider = scene.add
      .rectangle(MODAL_PADDING_X, footerY, MODAL_WIDTH - MODAL_PADDING_X * 2, 1, COLORS.emailSeparator)
      .setOrigin(0, 0);
    this.add(divider);
    if (email.disputeHook && !email.loggedAsDispute) {
      const btnWidth = DISPUTE_FORM_MODAL.readerFooterButtonWidth;
      const btnHeight = DISPUTE_FORM_MODAL.readerFooterButtonHeight;
      const btnX = MODAL_WIDTH - MODAL_PADDING_X - btnWidth;
      const btnY = footerY + (DISPUTE_FORM_MODAL.readerFooterHeight - btnHeight) / 2;
      const btn = scene.add
        .rectangle(btnX, btnY, btnWidth, btnHeight, COLORS.buttonPrimary)
        .setOrigin(0, 0)
        .setInteractive({ useHandCursor: true });
      this.add(btn);
      const label = scene.add.text(
        btnX + btnWidth / 2,
        btnY + btnHeight / 2,
        COPY.dispute.readerActionButton,
        {
          fontFamily: FONTS.ui,
          fontSize: DISPUTE_FORM_MODAL.readerFooterFontSize,
          color: toHexColor(COLORS.buttonPrimaryText),
          fontStyle: 'bold',
        },
      ).setOrigin(0.5, 0.5);
      this.add(label);
      const hook = email.disputeHook;
      btn.on('pointerdown', () => {
        this.bus?.emit('dispute:email-action-clicked', { emailId: email.id, hook });
        this.dismiss();
      });
    } else if (email.loggedAsDispute) {
      const badgeX = MODAL_PADDING_X;
      const badgeY = footerY + (DISPUTE_FORM_MODAL.readerFooterHeight - DISPUTE_FORM_MODAL.inboxBadgeHeight) / 2;
      const pill = scene.add
        .rectangle(badgeX, badgeY, DISPUTE_FORM_MODAL.inboxBadgeWidth, DISPUTE_FORM_MODAL.inboxBadgeHeight, COLORS.badgeLogged)
        .setOrigin(0, 0);
      this.add(pill);
      const pillLabel = scene.add.text(
        badgeX + DISPUTE_FORM_MODAL.inboxBadgeWidth / 2,
        badgeY + DISPUTE_FORM_MODAL.inboxBadgeHeight / 2,
        COPY.dispute.readerLoggedBadge,
        {
          fontFamily: FONTS.ui,
          fontSize: DISPUTE_FORM_MODAL.inboxBadgeFontSize,
          color: toHexColor(COLORS.buttonPrimaryText),
          fontStyle: 'bold',
        },
      ).setOrigin(0.5, 0.5);
      this.add(pillLabel);
    }
  }

  private renderActionFooter(scene: Phaser.Scene, email: Email): void {
    if (!this.bus) return;
    const category = email.category as EmailCategory;
    const actions = actionsForCategory(category);
    if (actions.length === 0) return;

    const footerY = MODAL_HEIGHT - EMAIL_ACTION_FOOTER.height;
    const divider = scene.add
      .rectangle(MODAL_PADDING_X, footerY, MODAL_WIDTH - MODAL_PADDING_X * 2, 1, COLORS.emailSeparator)
      .setOrigin(0, 0);
    this.add(divider);

    const widths = actions.map((a) => EMAIL_ACTION_FOOTER.buttonWidthByAction[a]);
    const totalWidth = widths.reduce((s, w) => s + w, 0)
      + EMAIL_ACTION_FOOTER.buttonGap * (actions.length - 1);
    const buttonY = footerY + (EMAIL_ACTION_FOOTER.height - EMAIL_ACTION_FOOTER.buttonHeight) / 2;
    let cursorX = MODAL_WIDTH - MODAL_PADDING_X - totalWidth;

    actions.forEach((action, i) => {
      const w = widths[i];
      const isPrimary = i === 0;
      const fill = isPrimary ? COLORS.buttonPrimary : COLORS.windowChrome;
      const btn = scene.add
        .rectangle(cursorX, buttonY, w, EMAIL_ACTION_FOOTER.buttonHeight, fill)
        .setOrigin(0, 0)
        .setStrokeStyle(1, COLORS.windowBorder)
        .setInteractive({ useHandCursor: true });
      this.add(btn);
      const label = scene.add.text(
        cursorX + w / 2,
        buttonY + EMAIL_ACTION_FOOTER.buttonHeight / 2,
        ACTION_LABELS[action],
        {
          fontFamily: FONTS.ui,
          fontSize: EMAIL_ACTION_FOOTER.buttonFontSize,
          color: toHexColor(isPrimary ? COLORS.buttonPrimaryText : COLORS.textPrimary),
          fontStyle: isPrimary ? 'bold' : 'normal',
        },
      ).setOrigin(0.5, 0.5);
      this.add(label);
      btn.on('pointerover', () => btn.setFillStyle(isPrimary ? COLORS.buttonHover : COLORS.windowTitleBar));
      btn.on('pointerout', () => btn.setFillStyle(fill));
      btn.on('pointerdown', () => this.handleActionClick(email.id, category, action));
      cursorX += w + EMAIL_ACTION_FOOTER.buttonGap;
    });
  }

  private handleActionClick(emailId: string, category: EmailCategory, action: EmailAction): void {
    if (!this.bus) return;
    if (action === 'reply') {
      this.openCompose(emailId, category);
      return;
    }
    const correctness = classifyAction(category, action);
    this.bus.emit('email:action-taken', { emailId, action, correctness });
    this.showActionToast(action);
    this.dismiss();
  }

  private openCompose(emailId: string, category: EmailCategory): void {
    if (this.composeOverlay) return;

    type ChoiceRow = { body: string; correct: boolean | null };
    const challenge = this.email.replyChallenge;
    const choices: ChoiceRow[] = challenge
      ? challenge.options.map((o) => ({ body: o.body, correct: o.correct }))
      : (REPLY_TEMPLATES[category] ?? []).map((t) => ({ body: t.body, correct: null }));

    if (choices.length === 0) {
      this.sendReply(emailId, category, 'Acknowledged. Will follow up.', null);
      return;
    }
    const overlay = this.scene.add.container(0, 0);
    overlay.setDepth(DEPTHS.modal + 5);

    // Opaque backdrop fully covers the original email body + action footer
    // (was a translucent dim before — the original content bled through and
    // hit areas of the action buttons stayed live behind the overlay).
    const bg = this.scene.add
      .rectangle(0, 0, MODAL_WIDTH, MODAL_HEIGHT, COLORS.windowBody, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.windowBorder)
      .setInteractive();
    overlay.add(bg);

    const PAD = 20;
    const ROW_GAP = 10;
    const ROW_H = 64;
    const innerW = MODAL_WIDTH - PAD * 2;

    const header = this.scene.add.text(PAD, PAD, `Reply to ${this.email.from}`, {
      fontFamily: FONTS.ui,
      fontSize: '14px',
      color: toHexColor(COLORS.textMuted),
    }).setOrigin(0, 0);
    overlay.add(header);

    const subjLine = this.email.subject.toLowerCase().startsWith('re:')
      ? this.email.subject
      : `Re: ${this.email.subject}`;
    const subject = this.scene.add.text(PAD, PAD + 22, subjLine, {
      fontFamily: FONTS.ui,
      fontSize: '16px',
      color: toHexColor(COLORS.textPrimary),
      fontStyle: 'bold',
      wordWrap: { width: innerW },
    }).setOrigin(0, 0);
    overlay.add(subject);

    let rowY = PAD + 56;

    if (challenge) {
      // Mark up the hint so AR terms (aging, bucket, dispute) become clickable
      // definitions while the player reads where to look.
      if (this.tooltip && this.bus) {
        renderMarkedText(
          this.scene,
          this.bus,
          this.tooltip,
          overlay,
          PAD,
          rowY,
          autoMark(`↗ ${challenge.lookupHint}`),
          {
            fontFamily: FONTS.ui,
            fontSize: '12px',
            color: toHexColor(COLORS.accentWarn),
            fontStyle: 'italic',
          },
          { wrapWidth: innerW },
        );
        rowY += 38;
      } else {
        const lookup = this.scene.add.text(PAD, rowY, `↗ ${challenge.lookupHint}`, {
          fontFamily: FONTS.ui,
          fontSize: '12px',
          color: toHexColor(COLORS.accentWarn),
          fontStyle: 'italic',
          wordWrap: { width: innerW },
        }).setOrigin(0, 0);
        overlay.add(lookup);
        rowY += lookup.height + 8;
      }
    }

    const subhint = this.scene.add.text(PAD, rowY, 'Pick a response to send:', {
      fontFamily: FONTS.ui,
      fontSize: '12px',
      color: toHexColor(COLORS.textMuted),
      fontStyle: 'italic',
    }).setOrigin(0, 0);
    overlay.add(subhint);
    rowY += 22;

    for (const choice of choices) {
      const row = this.scene.add
        .rectangle(PAD, rowY, innerW, ROW_H, COLORS.windowChrome)
        .setOrigin(0, 0)
        .setStrokeStyle(1, COLORS.windowBorder)
        .setInteractive({ useHandCursor: true });
      overlay.add(row);
      const body = this.scene.add.text(PAD + 12, rowY + ROW_H / 2, choice.body, {
        fontFamily: FONTS.ui,
        fontSize: '13px',
        color: toHexColor(COLORS.textPrimary),
        wordWrap: { width: innerW - 24 },
      }).setOrigin(0, 0.5);
      overlay.add(body);
      row.on('pointerover', () => row.setFillStyle(COLORS.windowTitleBar));
      row.on('pointerout', () => row.setFillStyle(COLORS.windowChrome));
      row.on('pointerdown', () => this.sendReply(emailId, category, choice.body, choice.correct));
      rowY += ROW_H + ROW_GAP;
    }

    const cancelLabel = this.scene.add.text(
      MODAL_WIDTH - PAD,
      MODAL_HEIGHT - PAD,
      'Cancel · ESC',
      {
        fontFamily: FONTS.ui,
        fontSize: '13px',
        color: toHexColor(COLORS.textMuted),
      },
    ).setOrigin(1, 1).setInteractive({ useHandCursor: true });
    cancelLabel.on('pointerdown', () => this.closeCompose());
    overlay.add(cancelLabel);

    this.add(overlay);
    this.composeOverlay = overlay;
    // Compose sits above the modal body — propagate at modal+5 so the
    // option rows and the bg fill all hit-test above modal chrome and
    // (importantly) above the underlying app chromeRects.
    this.propagateDepth(overlay, DEPTHS.modal + 5);
  }

  private sendReply(
    emailId: string,
    category: EmailCategory,
    body: string,
    challengeCorrect: boolean | null,
  ): void {
    if (!this.bus) return;
    // If the email had a reply challenge, the chosen option's correctness
    // overrides the category default — getting the right answer is +5/-2
    // territory, not the +3/0 of generic Reply.
    const correctness = challengeCorrect === null
      ? classifyAction(category, 'reply')
      : challengeCorrect ? 'correct' : 'wrong';
    this.bus.emit('email:action-taken', { emailId, action: 'reply', correctness, replyBody: body });
    this.showActionToast('reply');
    this.dismiss();
  }

  private closeCompose(): void {
    if (!this.composeOverlay) return;
    this.composeOverlay.destroy();
    this.composeOverlay = null;
  }

  private showActionToast(action: EmailAction): void {
    const messages: Record<EmailAction, string> = {
      reply: 'Reply sent.',
      archive: 'Archived.',
      'forward-collections': 'Forwarded to collections.',
      acknowledge: 'Got it.',
    };
    // Capture the scene locally — the modal is about to dismiss, which nulls
    // `this.scene`. The toast is parented to the scene (not to the modal
    // container) so it must own its own scene reference for the deferred
    // fade-out tween.
    const scene = this.scene;
    const text = scene.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT - 96,
      messages[action],
      {
        fontFamily: FONTS.ui,
        fontSize: '14px',
        color: toHexColor(COLORS.textPrimary),
        backgroundColor: toHexColor(COLORS.windowChrome),
        padding: { x: 16, y: 10 },
        fontStyle: 'bold',
      },
    ).setOrigin(0.5, 0.5).setDepth(DEPTHS.modal + 10).setAlpha(0);
    scene.tweens.add({
      targets: text,
      alpha: 1,
      duration: 120,
      ease: 'Quad.easeOut',
    });
    scene.time.delayedCall(1400, () => {
      if (!scene.tweens || !text.active) return;
      scene.tweens.add({
        targets: text,
        alpha: 0,
        y: text.y - 14,
        duration: 320,
        onComplete: () => text.destroy(),
      });
    });
  }

  private dismiss(): void {
    if (this.dismissed) return;
    this.dismissed = true;
    AudioManager.getInstance().playModalClose();
    this.teardown();
    this.onDismissCallback();
    this.destroy();
  }

  private teardown(): void {
    this.scene?.input.keyboard?.off('keydown', this.escHandler);
  }
}
